"use client";
import Pagination from "@/components/pagination/pagination";
import { Items } from "@/components/items/items";
import { SortDropDown } from "@/components/sortDropdown/sortDropdown";
import { useTranslations } from "next-intl";
import pom from "@/utils/pom";
import { applyDataTestId } from "@/utils/testUtils";
import { useEffect, useRef } from "react";
import useSWR from "swr";
import { getRetailCategoryItemsAction } from "../actions";

type RetailCategoryItemsActionResult = Awaited<
  ReturnType<typeof getRetailCategoryItemsAction>
>;

export const CategoryPage = ({
  merchantId,
  searchParams,
  category,
  itemTemplate = "Image Focused",
  items = [],
  pageSize,
  currentPage,
  totalPages,
  totalItemCount,
  merchantName,
  layout = "default",
}: any) => {
  const t = useTranslations("CategoryPage");
  const headerRef = useRef<HTMLHeadingElement>(null);

  const { data: swrData } = useSWR<RetailCategoryItemsActionResult>(
    merchantId && category?.id
      ? [
          "retailCategoryItems",
          merchantId,
          category.id,
          JSON.stringify(searchParams ?? {}),
        ]
      : null,
    () =>
      getRetailCategoryItemsAction(merchantId, category.id, searchParams ?? {}),
    {
      fallbackData: {
        data: {
          elements: items,
          currentPage,
          totalPages,
          totalItemCount,
        },
      },
      revalidateOnMount: false,
      revalidateOnFocus: true,
    }
  );

  const activeItems = swrData?.data?.elements ?? items;
  const activeCurrentPage = swrData?.data?.currentPage ?? currentPage;
  const activeTotalPages = swrData?.data?.totalPages ?? totalPages;
  const activeTotalItemCount = swrData?.data?.totalItemCount ?? totalItemCount;

  useEffect(() => {
    if (headerRef.current) {
      headerRef.current.focus();
    }
  }, [category.id]);

  const headerId = `${category.name.replaceAll(/\s+/g, "-")}-header`;
  return (
    <div className="flex-1 flex flex-col px-6 sm:px-12 pb-12 pt-6">
      <div className="flex flex-col sm:flex-row w-full  justify-between gap-4 mb-4">
        <h1
          ref={headerRef}
          id={headerId}
          tabIndex={-1}
          className="font-semibold text-[30px] leading-[45px] outline-none"
          data-testid={applyDataTestId(pom.retailCategories.categoryHeader)}
        >
          {category.name}
        </h1>
        <SortDropDown />
      </div>
      {activeItems.length > 0 ? (
        <Items
          {...{
            itemTemplate,
            items: activeItems,
            merchantName,
            layout,
          }}
        />
      ) : (
        <p className="text-[20px] text-[#4B5563] font-normal">
          {t("noItemsForSale")}
        </p>
      )}
      <Pagination
        totalPages={activeTotalPages}
        currentPage={activeCurrentPage}
        pageSize={pageSize}
        itemCount={activeItems.length}
        totalItemCount={activeTotalItemCount}
      />
    </div>
  );
};
